import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import Home from "./pages/Home";
import { Toaster } from "./components/ui/sonner";
import { CookieConsent } from "./components/sections";

// Route-level code splitting: every page below is fetched only when its
// route is visited, keeping the homepage's initial bundle lean. Home stays
// a static import above so critical content is available immediately.
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ForProducers = lazy(() => import("./pages/ForProducers"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const HowToImportItalianFood = lazy(() => import("./pages/BlogPost_HowToImport"));
const BlogPostOliveOil = lazy(() => import("./pages/BlogPost_OliveOil"));
const BlogPostExportOffice = lazy(() => import("./pages/BlogPost_ExportOffice"));
const BlogPostCanada = lazy(() => import("./pages/BlogPost_Canada"));
const BlogPostAustralia = lazy(() => import("./pages/BlogPost_Australia"));
const BlogPostMiddleEast = lazy(() => import("./pages/BlogPost_MiddleEast"));
const BlogPostCuredMeats = lazy(() => import("./pages/BlogPost_CuredMeats"));
const BlogPostCheeseUSA = lazy(() => import("./pages/BlogPost_CheeseUSA"));
const BlogPostMOQ = lazy(() => import("./pages/BlogPost_MOQ"));
const BlogPostProducerCertifications = lazy(() => import("./pages/BlogPost_ProducerCertifications"));
const BlogPostPecorinoRomano = lazy(() => import("./pages/BlogPost_PecorinoRomano"));
const BlogPostMixedPallet = lazy(() => import("./pages/BlogPost_MixedPallet"));
const BlogPostParmigianoReggiano = lazy(() => import("./pages/BlogPost_ParmigianoReggiano"));
const BlogPostGranaPadano = lazy(() => import("./pages/BlogPost_GranaPadano"));
const BlogPostPrivateLabelWhenToStart = lazy(() => import("./pages/BlogPost_PrivateLabelWhenToStart"));
const BlogPostExWorksFOB = lazy(() => import("./pages/BlogPost_ExWorksFOB"));
const BlogPostProsciuttoParmaVsSanDaniele = lazy(() => import("./pages/BlogPost_ProsciuttoParmaVsSanDaniele"));
const AuthorGiorgio = lazy(() => import("./pages/AuthorGiorgio"));
const CategoryOliveOil = lazy(() => import("./pages/CategoryOliveOil"));
const CategoryPasta = lazy(() => import("./pages/CategoryPasta"));
const CategoryPastryBakery = lazy(() => import("./pages/CategoryPastryBakery"));
const CategoryCuredMeats = lazy(() => import("./pages/CategoryCuredMeats"));
const CategoryGermanWholesale = lazy(() => import("./pages/CategoryGermanWholesale"));
const BrandSanChimento = lazy(() => import("./pages/BrandSanChimento"));
const BrandLeDolciSorelle = lazy(() => import("./pages/BrandLeDolciSorelle"));
const BrandItalianaFormaggi = lazy(() => import("./pages/BrandItalianaFormaggi"));
const BrandsHub = lazy(() => import("./pages/BrandsHub"));
const CategoryCheese = lazy(() => import("./pages/CategoryCheese"));
const MarketEurope = lazy(() => import("./pages/MarketEurope"));
const MarketUK = lazy(() => import("./pages/MarketUK"));
const MarketCanada = lazy(() => import("./pages/MarketCanada"));
const MarketAustralia = lazy(() => import("./pages/MarketAustralia"));
const MarketMiddleEast = lazy(() => import("./pages/MarketMiddleEast"));
const MarketSouthAmerica = lazy(() => import("./pages/MarketSouthAmerica"));
const MarketsHub = lazy(() => import("./pages/MarketsHub"));
const CategoriesHub = lazy(() => import("./pages/CategoriesHub"));
const PrivateLabel = lazy(() => import("./pages/PrivateLabel"));
const Services = lazy(() => import("./pages/Services"));
const ServiceStrategicSourcing = lazy(() => import("./pages/ServiceStrategicSourcing"));
const MarketUSA = lazy(() => import("./pages/MarketUSA"));
const CaseStudiesIndex = lazy(() => import("./pages/CaseStudiesIndex"));
const CaseStudyBellaLodi = lazy(() => import("./pages/CaseStudyBellaLodi"));
const CaseStudyLocasSeafood = lazy(() => import("./pages/CaseStudyLocasSeafood"));
const CaseStudyOliveOilNetherlands = lazy(() => import("./pages/CaseStudyOliveOilNetherlands"));
const InformativaPrivacy = lazy(() => import("./pages/InformativaPrivacy"));
const ToolImportCostCalculator = lazy(() => import("./pages/ToolImportCostCalculator"));
const ChromeExtensionPrivacy = lazy(() => import("./pages/ChromeExtensionPrivacy"));
const Glossary = lazy(() => import("./pages/Glossary"));
const NotFound = lazy(() => import("./pages/NotFound"));

function CataloghiRedirect() {
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '');
    // Bare /cataloghi or /cataloghi/ goes straight to the catalog index
    if (path === '/cataloghi') {
      window.location.replace('/api/cataloghi/cataloghi-1.html');
      return;
    }
    window.location.replace('/api' + window.location.pathname + window.location.search + window.location.hash);
  }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
        <CookieConsent />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();

  return (
    <ErrorBoundary key={location.pathname}>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/for-producers" element={<ForProducers />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/informativa-privacy-clienti-fornitori" element={<InformativaPrivacy />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/how-to-import-italian-food-to-the-usa" element={<HowToImportItalianFood />} />
          <Route path="/blog/italian-olive-oil-wholesale-usa" element={<BlogPostOliveOil />} />
          <Route path="/blog/what-is-italian-export-office" element={<BlogPostExportOffice />} />
          <Route path="/blog/how-to-import-italian-food-to-canada" element={<BlogPostCanada />} />
          <Route path="/blog/sourcing-italian-specialty-foods-australia" element={<BlogPostAustralia />} />
          <Route path="/blog/italian-food-importer-middle-east" element={<BlogPostMiddleEast />} />
          <Route path="/blog/italian-cured-meats-wholesale-usa" element={<BlogPostCuredMeats />} />
          <Route path="/blog/italian-cheese-wholesale-usa" element={<BlogPostCheeseUSA />} />
          <Route path="/blog/minimum-order-quantity-italian-food" element={<BlogPostMOQ />} />
          <Route path="/blog/italian-food-producer-certifications" element={<BlogPostProducerCertifications />} />
          <Route path="/blog/pecorino-romano-pdo-wholesale-sourcing" element={<BlogPostPecorinoRomano />} />
          <Route path="/blog/mixed-pallet-freight-cost-italian-food" element={<BlogPostMixedPallet />} />
          <Route path="/blog/parmigiano-reggiano-wholesale-usa" element={<BlogPostParmigianoReggiano />} />
          <Route path="/blog/grana-padano-wholesale-usa" element={<BlogPostGranaPadano />} />
          <Route path="/blog/private-label-italian-food-when-to-start" element={<BlogPostPrivateLabelWhenToStart />} />
          <Route path="/blog/ex-works-fob-italian-food-import" element={<BlogPostExWorksFOB />} />
          <Route path="/blog/prosciutto-di-parma-vs-san-daniele" element={<BlogPostProsciuttoParmaVsSanDaniele />} />
          <Route path="/author/giorgio-morocica" element={<AuthorGiorgio />} />
          <Route path="/categories/italian-olive-oil" element={<CategoryOliveOil />} />
          <Route path="/categories/italian-pasta" element={<CategoryPasta />} />
          <Route path="/categories/italian-pastry-bakery" element={<CategoryPastryBakery />} />
          <Route path="/categories/italian-cured-meats" element={<CategoryCuredMeats />} />
          <Route path="/de/italienische-lebensmittel-grosshandel" element={<CategoryGermanWholesale />} />
          <Route path="/brands/san-chimento" element={<BrandSanChimento />} />
          <Route path="/brands/le-dolci-sorelle" element={<BrandLeDolciSorelle />} />
          <Route path="/brands/italiana-formaggi" element={<BrandItalianaFormaggi />} />
          <Route path="/brands" element={<BrandsHub />} />
          <Route path="/categories/italian-cheese" element={<CategoryCheese />} />
          <Route path="/categories" element={<CategoriesHub />} />
          <Route path="/private-label" element={<PrivateLabel />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/strategic-sourcing" element={<ServiceStrategicSourcing />} />
          <Route path="/markets/usa" element={<MarketUSA />} />
          <Route path="/markets/europe" element={<MarketEurope />} />
          <Route path="/markets/uk" element={<MarketUK />} />
          <Route path="/markets/canada" element={<MarketCanada />} />
          <Route path="/markets/australia" element={<MarketAustralia />} />
          <Route path="/markets/middle-east" element={<MarketMiddleEast />} />
          <Route path="/markets/south-america" element={<MarketSouthAmerica />} />
          <Route path="/markets" element={<MarketsHub />} />
          <Route path="/case-studies" element={<CaseStudiesIndex />} />
          <Route path="/case-studies/bella-lodi-cheese-us-distributor" element={<CaseStudyBellaLodi />} />
          <Route path="/case-studies/locas-seafood-saudi-arabia-sfda" element={<CaseStudyLocasSeafood />} />
          <Route path="/case-studies/italian-olive-oil-netherlands-online-retailer" element={<CaseStudyOliveOilNetherlands />} />
          <Route path="/tools/italian-food-import-cost-calculator" element={<ToolImportCostCalculator />} />
          <Route path="/chrome-extension-privacy" element={<ChromeExtensionPrivacy />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/cataloghi" element={<CataloghiRedirect />} />
          <Route path="/cataloghi/*" element={<CataloghiRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;